import { throwError } from 'rxjs';
import * as XLSX from 'xlsx';

import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';

import { HttpServerResponse } from '@app/core/models/response.model';
import { FileResponse } from '../models/file-response.model';
import { SelectOption } from '../models/select-option.model';

export declare type InputDataEncoding = 'base64' | 'binary' | 'buffer' | 'file' | 'array' | 'string';

/**
 * Transforma el error y lo retorna a la petición
 *
 * @param error - Error procesado por la función
 */
export function transformError(error: HttpErrorResponse | string) {
  let errorMessage = 'Ha ocurrido un error al realizar la petición';
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error.error instanceof ErrorEvent) {
    errorMessage = `Error: ${error.error.message}`;
  } else if (error.status) {
    errorMessage = `La respuesta fallo por: ${error.status} ${error.statusText}`;
  }

  return throwError(() => new Error(errorMessage));
}

/**
 * Valida si un formaluario es valido y marca los campos requeridos o con reglas
 *
 * @param form - formulario a validar
 */
export function validateForm(form: FormGroup): boolean {
  if (form.invalid) {
    markFormGroupTouched(form);
    return false;
  }

  return true;
}

/**
 * Valida si un formaluario o varios son validos y marca los campos requeridos o con reglas
 *
 * @param forms - Listado de formularios a validar
 * @returns Valida si todos los formularios son validos
 */
export function validateForms(forms: FormGroup[]): boolean {
  let invalids = 0;
  for (const form of forms) {
    if (form.invalid) {
      markFormGroupTouched(form);
      invalids++;
    }
  }

  return invalids > 0;
}

/**
 * Marca todos los controles de un formulario como "toueched"
 *
 * @param formGroup - El formulario a validar
 */
export function markFormGroupTouched(formGroup: FormGroup): void {
  (Object as any).values(formGroup.controls).forEach((control: FormGroup) => {
    control.markAsTouched();

    if (control.controls) {
      markFormGroupTouched(control);
    }
  });
}

/**
 * Carga opciones para carga de Mat-Select
 *
 * @param options - Contenedor con las opciones a cargar
 * @param source - Información de origen para la carga de opciones
 * @param valueField - Propiedad que se tomara como Valor
 * @param viewValueField - Propiedades que se tomara como Descripción
 */
export function loadSelectOptions(options: SelectOption[], source: never[], valueField: string, viewValueField: string): void {
  for (const item of source) {
    options.push({ value: item[valueField], viewValue: `${item[viewValueField]}` });
  }
}

/**
 * Gener aun tiempo de espera para la ejecución de alguna acción
 *
 * @param ms - Indica de cuantos milisegundos sera el tiempo de espera
 * @returns Una promesa despues del tiempo de espera
 */
export function createDelay(ms: number): Promise<any> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Transforma una cadena de fecha en un formato de DD/MM/YYYY
 *
 * @param dateString - Fecha en formato caden
 * @returns Retorna la fecha formateada
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  return `${day}/${month}/${year}`;
}

/**
 * Realiza una descarga automatica de un archivo CSV en base 64
 *
 * @param fileData - Datos del archivo a descargar
 */
export function downloadCSV(fileData: FileResponse, dataEncoding: InputDataEncoding): void {
  const wb = XLSX.read(fileData.fileBase64, { codepage: 65001, raw: true, type: dataEncoding });
  XLSX.writeFile(wb, `${fileData.fileName}${fileData.fileType}`, { bookType: 'csv' });
}

export function instanceOfA<T>(obj: any, member: string): obj is T {
  return member in obj;
}

export function getDescriptionFromError(error: unknown): string {
  if (instanceOfA<HttpServerResponse<unknown>>(error, 'description')) return error.description;
  if (instanceOfA<HttpErrorResponse>(error, 'error')) return error.error.description;
  else return '';
}

export function padLeft(number: number, length: number, character: string = '0'): string {
  let result = String(number);
  for (let i = result.length; i < length; ++i) {
    result = character + result;
  }
  return result;
}
/**
 * Indica si un valor es indefinido o nulo
 *
 * @param object - Valor a evaluar
 * @returns Bool
 */
export function isNullOrUndefined(object: unknown): boolean {
  return object === undefined || object === null;
}
